<script setup lang="ts">
// /join?invite=<url>: join a space hosted on another hrth server, from your own.
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { describeError, inviteCode, inviteHost } from '../api/http'
import { decryptName, isEncrypted, keyFromInvite } from '../lib/metacrypto'
import type { InvitePreview } from '../api/types'
import { useSession } from '../stores/session'
import AuthFrame from '../components/AuthFrame.vue'

const route = useRoute()
const router = useRouter()
const session = useSession()
// The invite arrives in the fragment (#invite=...) so no server sees its key.
const invite = computed(
  () => new URLSearchParams(route.hash.slice(1)).get('invite') ?? String(route.query.invite ?? ''),
)
const host = computed(() => inviteHost(invite.value))
const preview = ref<InvitePreview | null>(null)
const error = ref<string | null>(null)
const busy = ref(false)

onMounted(async () => {
  if (session.status !== 'ready' || !host.value) return
  try {
    const p = await session.api.invitePreview(inviteCode(invite.value), host.value)
    if (isEncrypted(p.space_name)) {
      p.space_name = (await decryptName(keyFromInvite(invite.value) ?? undefined, 'space', p.space_name!)) ?? 'a private space'
    }
    preview.value = p
  } catch (e) {
    error.value = describeError(e)
  }
})

async function join(): Promise<void> {
  busy.value = true
  try {
    const space = await session.acceptInvite(invite.value)
    await router.replace(session.spaceLanding(space))
  } catch (e) {
    error.value = describeError(e)
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <AuthFrame testid="join-remote" caption="federation">
      <template v-if="session.status !== 'ready'">
        <h1 class="mb-2 text-2xl font-light">Sign in here first</h1>
        <p class="text-sm text-text-muted">This page joins a space on another server using your account on this one.</p>
      </template>
      <template v-else-if="preview">
        <p class="text-sm text-text-muted">
          <span class="text-text-primary">{{ preview.inviter.display_name }}</span> invited you to
        </p>
        <h1 class="mt-2 text-3xl font-light tracking-tight" data-testid="remote-space">{{ preview.space_name ?? preview.instance_name }}</h1>
        <p class="mono mt-2 text-text-muted">hosted on {{ host }} · you’ll join as {{ session.me }}</p>
        <p class="mt-4 text-xs text-text-muted">
          Your messages stay end-to-end encrypted; {{ host }} relays them like your own server does.
        </p>
        <button class="btn-primary mt-5 w-full" :disabled="busy" data-testid="join-remote-confirm" @click="join">
          {{ busy ? 'Joining…' : 'Join' }}
        </button>
      </template>
      <p v-else-if="error" class="text-sm text-error" data-testid="join-remote-error">{{ error }}</p>
      <p v-else class="text-sm text-text-muted">Contacting {{ host }}…</p>
  </AuthFrame>
</template>
